<template>
  <AppWithLoading v-slot:default="{ localLoading, loader }">
    <div class="agree-block">
      <VCheckbox v-model="agreeRecurrent" :label="$t('agreeRecurrent')" class="agree-block__item" />
      <VCheckbox
        v-model="agreePublic"
        :label="$t('agreePublic')"
        class="agree-block__item"
      >
        <template #label>
          <span class="mr-1">
            {{ $t('agreePublic') }}
            <a
              target="_blank"
              :href="offerLink"
              @click="handleLinkClick"
            >
              {{ $t('offer') }}
            </a>
          </span>
        </template>
      </VCheckbox>
    </div>
    <VBtn class="mt-6" :disabled="!canProceed" @click="loader(() => pay())">
      Оплатить
    </VBtn>
    <div class="mt-10 mb-4">
      <img style="width: 300px;" :src="require('@/assets/img/payments/all.svg')" />
    </div>
    <VDialog
      persistent
      max-width="882"
      content-class="dialog"
      v-model="dialog"
    >
      <VCard>
        <VCardTitle>
          Перенаправление
        </VCardTitle>
        <VCardText>
          Вы будете перенаправлены на страницу с оплатой...
          <Preloader />
        </VCardText>
      </VCard>
    </VDialog>
  </AppWithLoading>
</template>

<script>
import useAlfabank from '@/compositions/accounting/useAlfabank';
import {
  computed, ref, toRefs, watch,
} from '@vue/composition-api';
import useAccount from '@/compositions/accounting/useAccount';
import AppWithLoading from '@/components/AppWithLoading';
import Preloader from '@/components/Preloader.vue';
import { useI18n } from 'vue-i18n-composable';

export default {
  name: 'AlfabankUpgradePayment',
  components: { AppWithLoading, Preloader },
  props: {
    newCard: {
      type: Boolean,
      default: false,
    },
    plan: {
      type: Object,
      required: true,
    },
    methodId: {
      type: Number,
      default: null,
    },
    period: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit }) {
    const {
      plan, methodId, newCard, period,
    } = toRefs(props);
    const { account } = useAccount();
    const { createPayment, upgradePayment, renewPayment } = useAlfabank();
    const { plan: currentPlan } = account.value;
    const dialog = ref(false);

    const { locale } = useI18n();

    const offerLink = computed(() => (locale.value.toLowerCase() === 'ru'
      ? process.env.VUE_APP_RULES_URL
      : process.env.VUE_APP_CONFIDENTIAL_URL));

    async function pay() {
      if (currentPlan.default) {
        const result = await createPayment({
          priceId: plan.value.price.id,
          periodId: period.value.id,
          paymentMethodId: methodId.value,
        });
        if (result.redirect && !methodId.value) {
          dialog.value = true;
          document.location.href = result.redirect;
        } else if (result.redirect) {
          dialog.value = true;
          document.location.href = result.redirect;
        } else if (methodId.value) {
          emit('success');
        }
      } else if (account.value?.active === false) {
        const result = await renewPayment({
          priceId: plan.value.price.id,
          periodId: period.value.id,
          paymentMethodId: methodId.value,
        });
        if (result && result.redirect) {
          dialog.value = true;
          document.location.href = result.redirect;
        } else {
          emit('success');
        }
      } else if (account.value?.trialEnabled) {
        const result = await createPayment({
          priceId: plan.value.price.id,
          periodId: period.value.id,
          paymentMethodId: methodId.value,
        });
        if (result && result.redirect) {
          dialog.value = true;
          document.location.href = result.redirect;
        } else {
          emit('success');
        }
      } else {
        const result = await upgradePayment({
          planId: plan.value.id,
          paymentMethodId: newCard.value ? null : methodId.value,
        });
        if (result && result.redirect) {
          dialog.value = true;
          document.location.href = result.redirect;
        } else {
          emit('success');
        }
      }
    }

    const agreeRecurrent = ref(true);
    const agreePublic = ref(true);
    const linkClicked = ref(false);

    const canProceed = computed(() => {
      return agreePublic.value && agreeRecurrent.value && (newCard.value || methodId.value);
    });

    const previousAgreePublicValue = ref(agreePublic.value);

    watch(agreePublic, (newValue) => {
      if (linkClicked.value) {
        if (newValue !== previousAgreePublicValue.value) {
          agreePublic.value = previousAgreePublicValue.value;
        }
        linkClicked.value = false;
      } else {
        previousAgreePublicValue.value = newValue;
      }
    });

    function handleLinkClick(event) {
      previousAgreePublicValue.value = agreePublic.value;
      linkClicked.value = true;
      event.stopPropagation();
      event.preventDefault();
      window.open(offerLink.value, '_blank');
    }

    return {
      pay,
      dialog,
      offerLink,
      agreeRecurrent,
      agreePublic,
      canProceed,
      handleLinkClick,
    };
  },
};
</script>

<style scoped lang="scss">
.agree-block {
  &__item {
    width: fit-content;
  }
}
</style>

<i18n lang="yml" locale="ru">
agreeRecurrent: Я даю согласие на регулярные списание с карты
agreePublic: 'Принимаю условия публичной '
offer: оферты
confirm: Подтвердить оплату
</i18n>
<i18n lang="yml" locale="en">
agreeRecurrent: I understand that I will be charged on a regular basis
agreePublic: 'I accept the '
offer: terms and conditions
confirm: Confirm payment
</i18n>
